import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { clearModules } from "../../../features/userAccess/permissions/permissionsSlice";
import {
  useActivateSession,
  useSendOtp,
  useVerifyOtp,
} from "../../../hooks/mutations/useAuthMutations";
import type { RootState } from "../../../redux/store";
import { clearOtp, type BootstrapContext, type BootstrapTenant } from "../../authSlice";
import { useLoginBranding } from "../branding/loginBranding";

import { countryName, type CountryCode } from "@/config/countries";
import { phoneRegionOrDefault, toE164, toNationalDigits } from "@/utils/phone";

export type LoginStep = "enter" | "otp" | "select";

export type SelectOption = {
  value: string;
  label: string;
  description?: string;
};

export type LoginController = {
  loading: boolean;
  step: LoginStep;
  phone: string;
  otp: string;
  country: CountryCode;
  localError: string | null;
  resendTimer: number;
  loginLogoUrl?: string;
  otpSentText: string;
  mockOtp: string | null;
  selectionSummary: string | null;
  showTenantSelection: boolean;
  showContextSelection: boolean;
  tenantOptions: SelectOption[];
  contextOptions: SelectOption[];
  selectedTenantId: string;
  selectedContextKey: string;

  phoneRef: React.RefObject<HTMLInputElement | null>;
  otpRef: React.RefObject<HTMLInputElement | null>;

  setCountry: (country: CountryCode) => void;
  setPhoneFromInput: (value: string) => void;
  setOtpFromInput: (value: string) => void;
  setSelectedTenantId: (value: string) => void;
  setSelectedContextKey: (value: string) => void;

  handleSendOtp: () => void;
  handleVerifyOtp: () => void;
  handleActivateSession: () => void;
  handleResend: () => void;
  handleChangeNumber: () => void;

  onPhoneKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onOtpKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export function useLoginController(): LoginController {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sendOtpMutation = useSendOtp();
  const verifyOtpMutation = useVerifyOtp();
  const activateSessionMutation = useActivateSession();

  const auth = useSelector((s: RootState) => s.auth);

  const errorFromSlice = auth?.error ?? null;
  const otpSentTo = auth?.otpSentTo ?? null;
  const mockOtp = auth?.mockOtp ?? null;
  const authUser = auth?.user ?? null;
  const pendingBootstrap = auth?.pendingBootstrap ?? null;

  const loading =
    Boolean(auth?.loading) ||
    sendOtpMutation.isPending ||
    verifyOtpMutation.isPending ||
    activateSessionMutation.isPending;

  const branding = useLoginBranding();

  // Defaults to the resolved tenant's country, so a school in Dubai does not have to change the dial
  // code on every visit. The user can still override it — staff do not always hold a local number.
  // A school's country without phone metadata falls back to the platform default region; only the picker's starting
  // point changes, never the school's country.
  const [country, setCountry] = useState<CountryCode>(
    phoneRegionOrDefault(branding.countryCode),
  );
  const [phone, setPhone] = useState<string>("");
  const [step, setStep] = useState<LoginStep>("enter");
  const [otp, setOtp] = useState<string>("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [selectedContextKey, setSelectedContextKey] = useState<string>("");

  const loginLogoUrl = useMemo(() => {
    try {
      const rawSession = localStorage.getItem("auth_session_v1");
      if (rawSession) {
        const session = JSON.parse(rawSession) as {
          user?: { tenantId?: string; schoolId?: string };
        };
        const tenantId = session?.user?.tenantId ?? session?.user?.schoolId;
        if (tenantId) {
          return (
            localStorage.getItem(`sms.branding.logoUrl.${String(tenantId)}`) ??
            localStorage.getItem("sms.branding.logoUrl.default") ??
            undefined
          );
        }
      }

      return localStorage.getItem("sms.branding.logoUrl.default") ?? undefined;
    } catch {
      return undefined;
    }
  }, []);

  const phoneRef = useRef<HTMLInputElement | null>(null);
  const otpRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTimeout(() => phoneRef.current?.focus(), 10);
  }, []);

  // The host -> tenant lookup is asynchronous, so the first render may still be on the default.
  useEffect(() => {
    setCountry(phoneRegionOrDefault(branding.countryCode));
  }, [branding.countryCode]);

  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => otpRef.current?.focus(), 200);
    }
  }, [step]);

  useEffect(() => {
    let t: number | undefined;
    if (resendTimer > 0) {
      t = window.setTimeout(() => setResendTimer((s) => (s > 0 ? s - 1 : 0)), 1000);
    }
    return () => {
      if (t) window.clearTimeout(t);
    };
  }, [resendTimer]);

  useEffect(() => {
    if (pendingBootstrap) {
      setStep("select");
      return;
    }

    if (otpSentTo) {
      setStep("otp");
      return;
    }

    setStep("enter");
  }, [otpSentTo, pendingBootstrap]);

  useEffect(() => {
    if (errorFromSlice) setLocalError(errorFromSlice);
  }, [errorFromSlice]);

  useEffect(() => {
    if (authUser) {
      dispatch(clearModules());
    }
  }, [authUser, dispatch]);

  useEffect(() => {
    if (authUser && !pendingBootstrap) {
      navigate("/dashboard", { replace: true });
    }
  }, [authUser, navigate, pendingBootstrap]);

  useEffect(() => {
    if (!pendingBootstrap) {
      setSelectedTenantId("");
      setSelectedContextKey("");
      return;
    }

    const nextTenantId = pendingBootstrap.availableTenants[0]?.tenantId ?? "";
    const nextContext = pendingBootstrap.availableContexts.find(
      (context: BootstrapContext) => context.tenantId === nextTenantId,
    );

    setSelectedTenantId((current) => current || nextTenantId);
    setSelectedContextKey(
      (current) => current || (nextContext ? getContextKey(nextContext) : ""),
    );
  }, [pendingBootstrap]);

  const tenantOptions = useMemo<SelectOption[]>(() => {
    if (!pendingBootstrap) {
      return [];
    }

    return pendingBootstrap.availableTenants.map((tenant: BootstrapTenant) => ({
      value: tenant.tenantId,
      label: tenant.tenantName,
    }));
  }, [pendingBootstrap]);

  const contextOptions = useMemo<SelectOption[]>(() => {
    if (!pendingBootstrap || !selectedTenantId) {
      return [];
    }

    return pendingBootstrap.availableContexts
      .filter((context: BootstrapContext) => context.tenantId === selectedTenantId)
      .map((context: BootstrapContext) => ({
        value: getContextKey(context),
        label: buildContextLabel(context),
        description: context.roles?.length ? context.roles.join(", ") : context.dataScope,
      }));
  }, [pendingBootstrap, selectedTenantId]);

  const selectedContext = useMemo(() => {
    if (!pendingBootstrap || !selectedContextKey) {
      return null;
    }

    return (
      pendingBootstrap.availableContexts.find(
        (context: BootstrapContext) => getContextKey(context) === selectedContextKey,
      ) ?? null
    );
  }, [pendingBootstrap, selectedContextKey]);

  const showTenantSelection = useMemo(() => {
    return (pendingBootstrap?.availableTenants?.length ?? 0) > 1;
  }, [pendingBootstrap]);

  const showContextSelection = useMemo(() => {
    if (!pendingBootstrap) {
      return false;
    }

    const contextCountsByTenant = pendingBootstrap.availableContexts.reduce(
      (counts: Record<string, number>, context: BootstrapContext) => {
        counts[context.tenantId] = (counts[context.tenantId] ?? 0) + 1;
        return counts;
      },
      {} as Record<string, number>,
    );

    return (Object.values(contextCountsByTenant) as number[]).some((count) => count > 1);
  }, [pendingBootstrap]);

  const selectionSummary = useMemo(() => {
    if (!pendingBootstrap) {
      return null;
    }

    const resolvedUserName =
      pendingBootstrap.userName?.trim() ||
      pendingBootstrap.email?.trim() ||
      pendingBootstrap.phoneNumber?.trim() ||
      "Continue";

    const selectedTenantName =
      pendingBootstrap.availableTenants.find(
        (tenant: BootstrapTenant) => tenant.tenantId === selectedTenantId,
      )?.tenantName ??
      pendingBootstrap.availableTenants[0]?.tenantName ??
      "this tenant";

    const resolvedContextType =
      selectedContext?.contextType ??
      pendingBootstrap.availableContexts.find(
        (context: BootstrapContext) => context.tenantId === selectedTenantId,
      )?.contextType ??
      pendingBootstrap.availableContexts[0]?.contextType ??
      "this role";

    if (showTenantSelection && showContextSelection) {
      return `${resolvedUserName}, choose the tenant and role context for this session.`;
    }

    if (showTenantSelection) {
      return `${resolvedUserName}, choose the tenant for your ${resolvedContextType} session.`;
    }

    if (showContextSelection) {
      return `${resolvedUserName}, choose how to continue in ${selectedTenantName}.`;
    }

    return `${resolvedUserName}, continue to ${selectedTenantName} as ${resolvedContextType}.`;
  }, [
    pendingBootstrap,
    selectedContext,
    selectedTenantId,
    showContextSelection,
    showTenantSelection,
  ]);

  const setPhoneFromInput = (value: string) => {
    // No fixed length: national numbers run from 9 digits (AE, LK) to 10 (IN, BD, NP, PK), and the
    // numbering plan decides validity, not a truncation here.
    setPhone(toNationalDigits(value).slice(0, 15));
  };

  const setOtpFromInput = (value: string) => {
    setOtp(value.replace(/\D/g, "").slice(0, 6));
  };

  const handleSendOtp = () => {
    setLocalError(null);

    const e164 = toE164(phone, country);
    if (!e164) {
      setLocalError(`Enter a valid ${countryName(country)} mobile number.`);
      return;
    }

    // E.164 on the wire. The server stores exactly what it is given, so sending a national number
    // would make the account unfindable from any other country's dial code.
    sendOtpMutation.mutate(e164);
    setResendTimer(30);
    setOtp("");
  };

  const handleVerifyOtp = () => {
    setLocalError(null);
    if (otp.trim().length < 4) {
      setLocalError("Enter the OTP received on your phone.");
      return;
    }

    const e164 = toE164(phone, country);
    if (!e164) {
      setLocalError(`Enter a valid ${countryName(country)} mobile number.`);
      return;
    }

    verifyOtpMutation.mutate({ phoneNumber: e164, otpCode: otp.trim() });
  };

  const handleActivateSession = () => {
    setLocalError(null);

    if (!pendingBootstrap) {
      setLocalError("The verification session expired. Please request a new OTP.");
      return;
    }

    if (!selectedTenantId || !selectedContext) {
      if (showTenantSelection && showContextSelection) {
        setLocalError("Choose a tenant and role to continue.");
        return;
      }

      if (showTenantSelection) {
        setLocalError("Choose a tenant to continue.");
        return;
      }

      if (showContextSelection) {
        setLocalError("Choose a role to continue.");
        return;
      }

      setLocalError("The verification session is incomplete. Please request a new OTP.");
      return;
    }

    activateSessionMutation.mutate({
      bootstrapToken: pendingBootstrap.bootstrapToken,
      tenantId: selectedTenantId,
      activeContext: selectedContext.contextType,
      activeDomainEntityId: selectedContext.domainEntityId ?? null,
    });
  };

  const handleResend = () => {
    if (resendTimer > 0) return;

    const e164 = toE164(phone, country);
    if (!e164) {
      setLocalError(`Enter a valid ${countryName(country)} mobile number.`);
      return;
    }

    sendOtpMutation.mutate(e164);
    setResendTimer(30);
    setOtp("");
  };

  const handleChangeNumber = () => {
    dispatch(clearOtp());
    setStep("enter");
    setOtp("");
    setLocalError(null);
    setResendTimer(0);
    setSelectedTenantId("");
    setSelectedContextKey("");
    setTimeout(() => phoneRef.current?.focus(), 100);
  };

  const handleTenantSelection = (value: string) => {
    setSelectedTenantId(value);

    if (!pendingBootstrap) {
      setSelectedContextKey("");
      return;
    }

    const nextContext = pendingBootstrap.availableContexts.find(
      (context: BootstrapContext) => context.tenantId === value,
    );
    setSelectedContextKey(nextContext ? getContextKey(nextContext) : "");
  };

  const onPhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSendOtp();
  };

  const onOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (step === "select") {
        handleActivateSession();
      } else {
        handleVerifyOtp();
      }
    }

    if (step === "otp" && e.key === "Backspace" && otp.length === 0) {
      handleChangeNumber();
    }
  };

  const otpSentText = useMemo(() => {
    const display = otpSentTo ? String(otpSentTo) : toE164(phone, country);
    if (!display) return "";
    return `OTP sent to ${display}`;
  }, [otpSentTo, phone, country]);

  return {
    loading,
    step,
    phone,
    otp,
    country,
    localError,
    resendTimer,
    loginLogoUrl,
    otpSentText,
    mockOtp,
    selectionSummary,
    showTenantSelection,
    showContextSelection,
    tenantOptions,
    contextOptions,
    selectedTenantId,
    selectedContextKey,
    phoneRef,
    otpRef,
    setCountry,
    setPhoneFromInput,
    setOtpFromInput,
    setSelectedTenantId: handleTenantSelection,
    setSelectedContextKey,
    handleSendOtp,
    handleVerifyOtp,
    handleActivateSession,
    handleResend,
    handleChangeNumber,
    onPhoneKeyDown,
    onOtpKeyDown,
  };
}

function getContextKey(context: {
  tenantId: string;
  contextType: string;
  domainEntityId?: string | null;
}) {
  return `${context.tenantId}:${context.contextType}:${context.domainEntityId ?? "none"}`;
}

function buildContextLabel(context: {
  contextType: string;
  domainEntityId?: string | null;
}) {
  if (context.domainEntityId) {
    return `${context.contextType} (${context.domainEntityId.slice(0, 8)})`;
  }

  return context.contextType;
}
